/**
 * @file dismissedActivities.ts
 * @description Shared reactive state + AsyncStorage persistence for dismissed activity IDs.
 * Both the bell popover ("Mark all as read") and What's New feed dismiss buttons
 * write to the same reactive variable and AsyncStorage key, so dismissals survive
 * poll responses, tab switches, and page reloads.
 * @module utils
 */

import AsyncStorage from "@react-native-async-storage/async-storage";
import { makeVar, useReactiveVar } from "@apollo/client";

const DISMISSED_ACTIVITIES_KEY = "@whatsNew/dismissedActivityIds";

/**
 * Apollo reactive variable holding the set of dismissed activity IDs.
 * @remarks Both HeaderActivityPopover and WhatsNewContainer subscribe to this
 * via useDismissedActivityIds so they filter in real-time without prop drilling.
 */
const dismissedActivityIdsVar = makeVar<ReadonlySet<string>>(new Set<string>());

/**
 * Subscribes to the dismissed activity IDs reactive variable.
 * @returns Current set of dismissed activity IDs, re-renders on change
 * @remarks Wraps useReactiveVar so consumers don't need to import @apollo/client
 */
export function useDismissedActivityIds(): ReadonlySet<string> {
  return useReactiveVar(dismissedActivityIdsVar);
}

/**
 * Hydrates the reactive variable from AsyncStorage on app mount.
 * @remarks Call once on mount (e.g. in a top-level useEffect) so persisted
 * dismissals survive full page reloads and tab switches.
 */
export async function loadDismissedActivityIds(): Promise<void> {
  try {
    const raw = await AsyncStorage.getItem(DISMISSED_ACTIVITIES_KEY);
    if (raw) {
      dismissedActivityIdsVar(new Set(JSON.parse(raw) as string[]));
    }
  } catch {
    // AsyncStorage unavailable — start with empty set
  }
}

/**
 * Clears all dismissed activity IDs from both the reactive variable and AsyncStorage.
 * @remarks Called on org switch so dismissals from one org don't leak into another.
 */
export function clearDismissedActivityIds(): void {
  dismissedActivityIdsVar(new Set<string>());
  AsyncStorage.removeItem(DISMISSED_ACTIVITIES_KEY).catch(error => {
    console.warn("Failed to clear dismissed activity IDs from storage:", error);
  });
}

/**
 * Appends activity IDs to both the reactive variable and AsyncStorage.
 * @param ids - Activity IDs to mark as dismissed
 * @remarks Merges with existing set so concurrent calls from bell popover
 * and What's New don't overwrite each other.
 */
export function addDismissedActivityIds(ids: readonly string[]): void {
  const current = dismissedActivityIdsVar();
  const next = new Set([...current, ...ids]);
  dismissedActivityIdsVar(next);
  AsyncStorage.setItem(
    DISMISSED_ACTIVITIES_KEY,
    JSON.stringify([...next])
  ).catch(() => {});
}
